import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fetcher = (url) => api.get(url).then((r) => r.data);

const CATEGORIES = {
  income: ["Salary", "Freelance", "Investment", "Bonus", "Gift", "Other"],
  expense: ["Food", "Transport", "Housing", "Utilities", "Shopping", "Health", "Entertainment", "Education", "Debt", "Other"],
};

export default function AddTransaction() {
  const navigate = useNavigate();
  const { data: accounts } = useSWR("/accounts", fetcher);

  const [type, setType] = useState("expense");
  const [amount, setAmount] = useState("");
  const [accountId, setAccountId] = useState("");
  const [category, setCategory] = useState("Food");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!accountId) return toast.error("Pilih akun terlebih dahulu");
    if (!amount || Number(amount) <= 0) return toast.error("Masukkan jumlah yang valid");
    setSubmitting(true);
    try {
      await api.post("/transactions", {
        account_id: accountId,
        type,
        amount: Number(amount),
        category,
        note,
        date,
      });
      toast.success("Transaksi berhasil dicatat");
      mutate("/transactions");
      mutate("/accounts");
      mutate("/reports/summary");
      navigate("/history");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSubmitting(false);
    }
  };

  const cats = CATEGORIES[type];

  return (
    <div className="animate-fade-up max-w-2xl">
      <p className="label-caps">Record</p>
      <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-1">Add Transaction</h1>

      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="add-transaction-form">
        <div className="flex gap-2 border border-border p-1 w-fit">
          <button
            type="button"
            onClick={() => { setType("expense"); setCategory("Food"); }}
            data-testid="type-expense"
            className={`px-5 py-2 text-sm font-medium transition-colors ${type === "expense" ? "bg-loss text-white" : "text-muted-foreground hover:text-foreground"}`}
          >Expense</button>
          <button
            type="button"
            onClick={() => { setType("income"); setCategory("Salary"); }}
            data-testid="type-income"
            className={`px-5 py-2 text-sm font-medium transition-colors ${type === "income" ? "bg-gain text-white" : "text-muted-foreground hover:text-foreground"}`}
          >Income</button>
        </div>

        <div>
          <Label htmlFor="amount">Jumlah (IDR)</Label>
          <Input
            id="amount"
            type="number"
            placeholder="150000"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="input-amount"
            className="mt-2 font-mono text-lg h-12"
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Akun</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="mt-2 h-11" data-testid="select-account">
                <SelectValue placeholder="Pilih akun" />
              </SelectTrigger>
              <SelectContent>
                {(accounts || []).map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`account-option-${a.id}`}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Kategori</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-2 h-11" data-testid="select-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {cats.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="date">Tanggal</Label>
          <Input id="date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-2 h-11" data-testid="input-date" />
        </div>

        <div>
          <Label htmlFor="note">Catatan</Label>
          <Textarea id="note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Opsional" className="mt-2" data-testid="input-note" />
        </div>

        <Button type="submit" disabled={submitting} className="h-11 px-6 bg-foreground text-background hover:bg-foreground/90" data-testid="submit-transaction">
          {submitting ? "Menyimpan…" : "Simpan Transaksi"}
        </Button>
      </form>
    </div>
  );
}
